import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { CONTACT } from "../../lib/data";
import { useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { c } = useLang();
  const t = c.contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", serviceIdx: 0, purposeIdx: 0, message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.toastFill);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company,
        service: t.services[form.serviceIdx],
        purpose: t.purposes[form.purposeIdx],
        message: form.message,
      });
      toast.success(data.message || t.toastThanks);
      setForm({ name: "", email: "", company: "", serviceIdx: 0, purposeIdx: 0, message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t.toastErr);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-[var(--border)] py-3 font-sans text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/60 focus:border-[var(--accent)] focus:outline-none transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-[var(--bg-secondary)] border-t border-[var(--border)]">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <Reveal>
            <Overline>{t.overline}</Overline>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight mt-5 leading-[0.9]">
              {t.heading}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-sans text-base md:text-lg text-[var(--text-secondary)] mt-8 max-w-md leading-relaxed">
              {t.lead}
            </p>
            <div className="mt-12 flex flex-col gap-5">
              <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-[var(--text-primary)] group w-fit">
                <Mail size={18} className="text-[var(--accent)]" />
                <span className="font-sans link-underline">{CONTACT.email}</span>
              </a>
              <a data-testid="contact-phone" href={`tel:${CONTACT.phone}`} className="flex items-center gap-4 text-[var(--text-primary)] group w-fit">
                <Phone size={18} className="text-[var(--accent)]" />
                <span className="font-sans link-underline">{CONTACT.phone}</span>
              </a>
              <a data-testid="contact-whatsapp" href={`https://wa.me/${CONTACT.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-[var(--text-primary)] group w-fit">
                <MessageCircle size={18} className="text-[var(--accent)]" />
                <span className="font-sans link-underline">WhatsApp · {CONTACT.phone}</span>
              </a>
              <div className="flex items-center gap-4 text-[var(--text-secondary)]">
                <MapPin size={18} className="text-[var(--accent)]" />
                <span className="font-sans">{CONTACT.location}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-7">
            <div data-testid="contact-purpose-group" className="grid grid-cols-2 gap-3">
              {t.purposes.map((p, i) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`purpose-${i === 0 ? "quote" : "question"}`}
                  onClick={() => setForm((f) => ({ ...f, purposeIdx: i }))}
                  className={`py-3 rounded-full border font-mono text-xs uppercase tracking-[0.12em] transition-colors duration-300 ${
                    form.purposeIdx === i
                      ? "bg-[var(--accent)] text-[var(--bg-primary)] border-[var(--accent)]"
                      : "bg-transparent text-[var(--text-secondary)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
              <input data-testid="contact-name" className={field} placeholder={t.ph.name} value={form.name} onChange={set("name")} />
              <input data-testid="contact-company" className={field} placeholder={t.ph.company} value={form.company} onChange={set("company")} />
            </div>
            <input data-testid="contact-email-input" type="email" className={field} placeholder={t.ph.email} value={form.email} onChange={set("email")} />
            <select data-testid="contact-service" className={`${field} cursor-pointer`} value={form.serviceIdx} onChange={(e) => setForm((f) => ({ ...f, serviceIdx: Number(e.target.value) }))}>
              {t.services.map((s, i) => (
                <option key={s} value={i} className="bg-[var(--bg-primary)] text-[var(--text-primary)]">{s}</option>
              ))}
            </select>
            <textarea data-testid="contact-message" rows={4} className={`${field} resize-none`} placeholder={t.ph.message} value={form.message} onChange={set("message")} />
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="mt-2 inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] font-semibold hover:bg-[var(--accent-hover)] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : null}
              {loading ? t.sending : form.purposeIdx === 1 ? t.submitQuestion : t.submitQuote}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
